/**
 * CRM Follow-ups — the sales follow-up radar.
 *
 * Default view: due / upcoming follow-ups across all customers, sorted by soonest
 * planned date — so the team can see who needs a touch. Toggle to "all" for history.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, EmptyState, Spinner } from '../../components/ui';
import { Calendar, Building2, CheckSquare, ArrowRight } from '../../lib/icons';
import { useT } from '../../lib/i18n';
import { formatDate } from '../../lib/utils';
import { fetchFollowups } from '../../lib/crm-api';
import type { CrmFollowup } from '../../lib/crm-api';

export function FollowupsPage() {
  const t = useT();
  const [items, setItems] = useState<CrmFollowup[]>([]);
  const [loading, setLoading] = useState(true);
  const [dueOnly, setDueOnly] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchFollowups({ due_only: dueOnly, limit: 100 });
      setItems(res.items);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [dueOnly]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const todayStr = formatDate(new Date().toISOString());
  const isOverdue = (f: CrmFollowup) => !!f.next_followup_at && new Date(f.next_followup_at) < new Date();

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Filter bar */}
      <div className="border-b border-edge bg-surface-raised px-4 py-2.5 flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar size={15} className="text-fg-muted" />
          <span className="text-sm font-medium text-fg">{t('crm.followupRadar')}</span>
          <div className="flex-1" />
          <div className="flex items-center rounded-md border border-edge overflow-hidden">
            <button
              onClick={() => setDueOnly(true)}
              className={`px-2.5 py-1 text-xs transition-colors ${dueOnly ? 'bg-surface-muted text-fg font-medium' : 'text-fg-muted hover:bg-surface-muted'}`}
            >
              {t('crm.dueOnly')}
            </button>
            <button
              onClick={() => setDueOnly(false)}
              className={`px-2.5 py-1 text-xs transition-colors ${!dueOnly ? 'bg-surface-muted text-fg font-medium' : 'text-fg-muted hover:bg-surface-muted'}`}
            >
              {t('crm.allFollowups')}
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <Spinner className="h-6 w-6 text-fg-faint" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title={dueOnly ? t('crm.noFollowupsDue') : t('crm.noFollowups')}
            description={todayStr}
          />
        ) : (
          <div className="max-w-3xl mx-auto px-4 py-4 space-y-2">
            {items.map((f) => {
              const overdue = isOverdue(f);
              return (
                <Card
                  key={f.id}
                  className="p-3 cursor-pointer hover:bg-surface-muted transition-colors"
                  onClick={() => (window.location.hash = `#/crm/companies/${f.company_id}`)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-md bg-surface-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Building2 size={14} className="text-fg-muted" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-fg truncate">
                          {f.company_name || t('crm.customer')}
                        </span>
                        {f.outcome && <Badge variant="secondary">{f.outcome}</Badge>}
                        {f.next_followup_at && (
                          <Badge variant={overdue ? 'destructive' : 'warning'}>
                            {overdue ? t('crm.overdue') : t('crm.upcoming')}
                          </Badge>
                        )}
                      </div>
                      {f.purpose && (
                        <div className="text-xs text-fg-secondary mt-1">
                          <span className="text-fg-muted">{t('crm.purpose')}: </span>
                          {f.purpose}
                        </div>
                      )}
                      {f.result && <p className="text-[11px] text-fg-muted mt-0.5 line-clamp-2">{f.result}</p>}
                      {f.next_goal && (
                        <div className="text-[11px] text-fg-muted mt-1 flex items-center gap-1">
                          <CheckSquare size={11} className="text-fg-faint" />
                          {f.next_goal}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {f.next_followup_at && (
                        <div className={`text-xs font-medium ${overdue ? 'text-danger' : 'text-fg-secondary'}`}>
                          {formatDate(f.next_followup_at)}
                        </div>
                      )}
                      <div className="text-[10px] text-fg-faint mt-0.5">{formatDate(f.occurred_at)}</div>
                      <ArrowRight size={12} className="text-fg-faint inline-block mt-1" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
